import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { platform_wnstnToken } from 'store/platform/selectors';
import { platform_getWnstnToken } from 'store/platform/actions';
import useDisableBottomNavOnNewChallenge from 'components/BottomNavigation/useDisableBottomNavOnNewChallenge';
import { user_loggedIn } from 'store/user/selectors';
import { useConfigValue, ConfigKeys } from 'hooks/useDynamicConfig';
import { PROP_BOTTOM_NAV_HEIGHT, WNSTN_CLOSE_MESSAGE_TYPE, WNSTN_IFRAME_ORIGIN } from './wnstn-widget.constants';

interface WnstnUserParams {
    userId: string;
    userClass: string;
}

export interface WnstnAnchorRect {
    top: number;
}

export interface WnstnContextValue {
    isOpen: boolean;
    toggleOpen: () => void;
    setIsOpen: (open: boolean) => void;
    shouldRender: boolean;
    wnstnUserParams: WnstnUserParams | undefined;
    anchorRect: WnstnAnchorRect | null;
    setAnchorRect: (rect: WnstnAnchorRect | null) => void;
    bottomNavHeight: number;
}

export const WnstnContext = React.createContext<WnstnContextValue | undefined>(undefined);

export const useWnstnContext = (): WnstnContextValue => {
    const ctx = React.useContext(WnstnContext);
    if (!ctx) {
        throw new Error('useWnstnContext must be used within a WnstnProvider');
    }
    return ctx;
};

const decodeWnstnUserParams = (token: string): WnstnUserParams | undefined => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (typeof payload.wnstnUserId !== 'string' || typeof payload.wnstnUserClass !== 'string') return undefined;
        return { userId: payload.wnstnUserId, userClass: payload.wnstnUserClass };
    } catch {
        return undefined;
    }
};

export const WnstnProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const wnstnToken = useSelector(platform_wnstnToken);
    const isLoggedIn = useSelector(user_loggedIn);
    const wnstnUserParams = useMemo(() => (wnstnToken ? decodeWnstnUserParams(wnstnToken) : undefined), [wnstnToken]);
    const isWnstnEnabled = useConfigValue(ConfigKeys.WNSTN_ENABLED, false);
    const shouldRender = !!wnstnUserParams && isWnstnEnabled;

    useEffect(() => {
        if (isLoggedIn && isWnstnEnabled) {
            dispatch(platform_getWnstnToken() as any);
        }
    }, [dispatch, isLoggedIn, isWnstnEnabled]);

    const [isOpen, setIsOpen] = useState(false);
    const [anchorRect, setAnchorRect] = useState<WnstnAnchorRect | null>(null);
    const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);

    const { pathname } = useLocation();
    useEffect(() => {
        setIsOpen(false);
        setAnchorRect(null);
    }, [pathname]);

    const isOnNewChallengePage = useDisableBottomNavOnNewChallenge();
    const bottomNavHeight = isOnNewChallengePage ? 0 : PROP_BOTTOM_NAV_HEIGHT;

    useEffect(() => {
        const handleWnstnClose = (event: MessageEvent) => {
            if (event.origin !== WNSTN_IFRAME_ORIGIN || event.data?.type !== WNSTN_CLOSE_MESSAGE_TYPE) {
                return;
            }
            setIsOpen(false);
        };
        window.addEventListener('message', handleWnstnClose);
        return () => window.removeEventListener('message', handleWnstnClose);
    }, []);

    const value: WnstnContextValue = useMemo(
        () => ({
            isOpen,
            toggleOpen,
            setIsOpen,
            shouldRender,
            wnstnUserParams,
            anchorRect,
            setAnchorRect,
            bottomNavHeight,
        }),
        [isOpen, toggleOpen, shouldRender, wnstnUserParams, anchorRect, bottomNavHeight] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return <WnstnContext.Provider value={value}>{children}</WnstnContext.Provider>;
};
